/**
 * Payment Method Card Component
 * Displays a saved payment method
 */
import React from 'react';
import { CreditCard, Check } from 'lucide-react';

const PaymentMethodCard = ({ 
  paymentMethod, 
  isDefault = false, 
  onSetDefault,
  onEdit,
  onRemove,
  isLoading = false
}) => {
  const { id, brand, last4, expMonth, expYear } = paymentMethod;

  // Get card brand icon/color
  const getBrandColor = (brand) => {
    switch (brand.toLowerCase()) {
      case 'visa': return 'text-blue-400';
      case 'mastercard': return 'text-orange-400';
      case 'amex': return 'text-blue-300';
      default: return 'text-gray-400';
    }
  };

  const getBrandName = (brand) => {
    switch (brand.toLowerCase()) {
      case 'visa': return 'VISA';
      case 'mastercard': return 'Mastercard';
      case 'amex': return 'American Express';
      default: return brand;
    }
  };

  return (
    <div 
      className={`rounded-xl border p-4 transition-all ${
        isDefault 
          ? 'border-accent/50 bg-accent/5' 
          : 'border-white/10 bg-dark-100'
      }`}
    >
      <div className="flex items-start justify-between">
        {/* Card Info */}
        <div className="flex items-center gap-3">
          <div className={`w-12 h-8 rounded-lg bg-dark-200 flex items-center justify-center ${getBrandColor(brand)}`}>
            <CreditCard size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-white font-medium">{getBrandName(brand)}</span>
              <span className="text-gray-400">•••• {last4}</span>
            </div>
            <p className="text-sm text-gray-500">
              Expires {String(expMonth).padStart(2, '0')}/{expYear}
            </p>
          </div>
        </div>

        {/* Default Badge */}
        {isDefault && (
          <div className="flex items-center gap-1 px-2 py-1 bg-accent/20 rounded-full">
            <Check size={12} className="text-accent" />
            <span className="text-xs text-accent font-medium">Default</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-white/10">
        {!isDefault && (
          <button
            onClick={() => onSetDefault && onSetDefault(id)}
            disabled={isLoading}
            className="text-sm text-accent hover:text-accent-light transition-colors disabled:opacity-50"
          >
            Set Default
          </button>
        )}
        <button
          onClick={() => onEdit && onEdit(paymentMethod)}
          disabled={isLoading}
          className="text-sm text-gray-400 hover:text-white transition-colors disabled:opacity-50"
        >
          Edit
        </button>
        <button
          onClick={() => onRemove && onRemove(id)}
          disabled={isLoading || isDefault}
          className="text-sm text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

export default PaymentMethodCard;
